import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, assetUrl } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ArrowRight, UserPlus, Users, Shield, Calendar as CalIcon, Sparkles, Trophy, ChevronDown } from "lucide-react";
import { useMobileNav } from "../lib/MobileNavContext";

export const HERO_PROFESSIONAL_LOGO = "https://static.prod-images.emergentagent.com/jobs/d048b052-0eab-46e8-bcb5-6ba1987329ed/images/f4d0ab1154eca5dd4b35580d5b541a5fd981a6674522155cc041b55c36662136.png";
export const HERO_BRUSH_LOGO = "https://static.prod-images.emergentagent.com/jobs/d048b052-0eab-46e8-bcb5-6ba1987329ed/images/a43228aefae11a0e901e986f8edc6d6c5bfa2c490b4ec469e290d5507de0cb57.png";
export const HERO_ICON_LOGO = "https://customer-assets.emergentagent.com/job_d8fed49e-380f-4217-a8e9-486f1782ff60/artifacts/0qqhxw08_%7B76CE4EA0-7D80-4C78-9FAD-3B71E8B66535%7D.png";
export const HERO_GRUNGE_BG = "https://static.prod-images.emergentagent.com/jobs/d048b052-0eab-46e8-bcb5-6ba1987329ed/images/c88b1e581056ae6dac7934236d048cfc8f0a54ac4f29d247879761543a63ef12.png";

export const HERO_LOGO_PRESETS = [
    { k: "professional", l: "Professional wordmark", url: HERO_PROFESSIONAL_LOGO, blend: true },
    { k: "brush", l: "Brush wordmark", url: HERO_BRUSH_LOGO, blend: true },
    { k: "icon", l: "Rosterly icon", url: HERO_ICON_LOGO, blend: false },
    { k: "custom", l: "Custom upload", url: "", blend: false },
];

export const DEFAULT_HERO_CONFIG = {
    logo_preset: "professional",
    logo_custom_url: "",
    logo_size: "large",
    show_logo: true,
    hero_height: "standard",
    subtitle_size: "md",
    subtitle_font: "sans",
    background_mode: "none",
    show_subtitle: true,
    subtitle: "Built for the Future of Women's Hockey.",
    buttons: [
        { label: "Explore", action: "link", link: "/teams", style: "primary" },
        { label: "Sign In / Sign Up", action: "signup", link: "/login", style: "secondary" },
    ],
};

export const HERO_HEIGHTS = {
    compact: { wrap: "md:min-h-[200px]", pad: "py-6 md:py-6" },
    standard: { wrap: "md:min-h-[360px]", pad: "py-10 md:py-10" },
    tall: { wrap: "md:min-h-[480px]", pad: "py-14 md:py-14" },
};
const SUB_SIZE = { sm: "text-sm sm:text-base", md: "text-base sm:text-2xl", lg: "text-lg sm:text-3xl", xl: "text-xl sm:text-4xl" };
const SUB_FONT = { sans: "font-semibold", heading: "font-heading font-black uppercase tracking-tight", mono: "font-mono-stat font-bold" };

export const DEFAULT_QUICK_LINKS = [
    { label: "Standings", link: "/teams?tab=standings" },
    { label: "Schedule", link: "/teams?tab=schedule" },
    { label: "Leaderboards", link: "/teams?tab=leaderboards" },
    { label: "Contracts", link: "/teams?tab=contracts" },
    { label: "Draft", link: "/teams?tab=draft" },
    { label: "Rankings", link: "/teams?tab=rankings" },
    { label: "Movement", link: "/teams?tab=transfers" },
    { label: "Trades", link: "/teams?tab=trades" },
    { label: "Awards", link: "/teams?tab=awards" },
];

const LOGO_SIZE = {
    xsmall: "h-10 sm:h-12 md:h-14",
    small: "h-16 sm:h-20 md:h-24",
    medium: "h-24 sm:h-32 md:h-40",
    large: "h-32 sm:h-44 md:h-56",
    xlarge: "h-40 sm:h-56 md:h-72",
    xxlarge: "h-48 sm:h-64 md:h-80",
};

const HERO_STAT_ICON = (label) => {
    const l = (label || "").toLowerCase();
    if (l.includes("player")) return Users;
    if (l.includes("team")) return Shield;
    if (l.includes("game") || l.includes("schedule")) return CalIcon;
    if (l.includes("insight")) return Sparkles;
    return Trophy;
};

const logoUrlFor = (cfg) => {
    if (cfg.logo_preset === "custom") return cfg.logo_custom_url ? assetUrl(cfg.logo_custom_url) : "";
    return (HERO_LOGO_PRESETS.find((p) => p.k === cfg.logo_preset) || HERO_LOGO_PRESETS[0]).url;
};
const logoBlend = (cfg) => (HERO_LOGO_PRESETS.find((p) => p.k === cfg.logo_preset)?.blend) || false;

// Shared landing-page hero. Fully driven by `config` (admin-editable). `preview`
// disables real navigation so it can render inside the admin panel.
export function HomeHero({ config, preview = false }) {
    const cfg = { ...DEFAULT_HERO_CONFIG, ...(config || {}) };
    const { user } = useAuth();
    const { openDrawer } = useMobileNav();
    const { data: stats = [] } = useQuery({
        queryKey: ["hero-stats"],
        queryFn: () => api.get("/hero/stats").then((r) => r.data),
    });

    const logoUrl = logoUrlFor(cfg);
    const blend = logoBlend(cfg);
    const bgMode = cfg.background_mode;
    const customBg = bgMode === "custom" && cfg._bgImage ? cfg._bgImage : "";
    // Sign-up buttons disappear once the visitor is logged in (preview always shows them).
    const buttons = (cfg.buttons || []).filter((b) => b.label && !(b.action === "signup" && user && !preview));
    const Btn = ({ b, i }) => {
        const to = b.action === "signup" ? "/login?mode=signup" : (b.link || "/");
        const primary = b.style !== "secondary";
        const cls = primary
            ? "bg-[#7C3AED] hover:bg-[#9D4CDD] text-white shadow-[0_8px_30px_rgba(124,58,237,0.45)]"
            : "bg-white/5 hover:bg-white/15 text-white border border-white/25 backdrop-blur";
        const base = `inline-flex items-center gap-2.5 px-7 sm:px-9 py-3 sm:py-3.5 rounded-full font-bold uppercase tracking-[0.2em] text-xs sm:text-sm transition-colors ${cls}`;
        const inner = <>{b.label}{b.action === "signup" ? <UserPlus className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}</>;
        // "Explore" on mobile/tablet jumps to the bottom-nav Tabs drawer instead of navigating.
        const isExplore = b.action === "link" && (b.label || "").trim().toLowerCase() === "explore";
        if (isExplore && !preview) {
            return (
                <>
                    <button type="button" onClick={() => openDrawer("tabs")} className={`${base} lg:hidden`} data-testid={`hero-btn-${i}`}>
                        {b.label}<ChevronDown className="h-4 w-4" />
                    </button>
                    <Link to={to} className={`${base} hidden lg:inline-flex`} data-testid={`hero-btn-${i}-desktop`}>{inner}</Link>
                </>
            );
        }
        if (preview) return <span className={base} data-testid={`hero-btn-${i}`}>{inner}</span>;
        return <Link to={to} className={base} data-testid={`hero-btn-${i}`}>{inner}</Link>;
    };

    return (
        <div className={`relative overflow-hidden bg-[#0A0B10] ${(HERO_HEIGHTS[cfg.hero_height] || HERO_HEIGHTS.standard).wrap} flex items-center justify-center`} data-testid="hero-brush">
            {bgMode === "grunge" ? (
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_GRUNGE_BG})` }} aria-hidden="true" data-testid="hero-brush-bg" />
            ) : customBg ? (
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${customBg})` }} aria-hidden="true" data-testid="hero-brush-bg" />
            ) : (
                <div className="absolute inset-0" style={{ background: "radial-gradient(110% 90% at 50% 0%, rgba(124,58,237,0.22) 0%, transparent 60%)" }} aria-hidden="true" />
            )}
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,11,16,0.30) 0%, rgba(10,11,16,0.22) 45%, rgba(10,11,16,0.78) 100%)" }} />
            <div className={`relative w-full px-5 sm:px-8 ${(HERO_HEIGHTS[cfg.hero_height] || HERO_HEIGHTS.standard).pad} flex flex-col items-center text-center`}>
                {cfg.show_logo && logoUrl && (
                    <img src={logoUrl} alt="Rosterly" className={`${LOGO_SIZE[cfg.logo_size] || LOGO_SIZE.large} w-auto max-w-[92%] object-contain`} style={blend ? { mixBlendMode: "screen" } : undefined} data-testid="hero-brush-wordmark" />
                )}
                {cfg.show_subtitle && cfg.subtitle && (
                    <p className={`mt-3 sm:mt-5 text-white ${SUB_SIZE[cfg.subtitle_size] || SUB_SIZE.md} ${SUB_FONT[cfg.subtitle_font] || SUB_FONT.sans}`} data-testid="hero-brush-subtitle">{cfg.subtitle}</p>
                )}
                {buttons.length > 0 && (
                    <div className="mt-6 sm:mt-8 flex flex-wrap items-center justify-center gap-3" data-testid="hero-buttons">
                        {buttons.map((b, i) => <Btn key={i} b={b} i={i} />)}
                    </div>
                )}
                {stats.length > 0 && (
                    <div className="mt-6 sm:mt-9 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 w-full max-w-3xl" data-testid="hero-stats">
                        {stats.map((s, i) => {
                            const Icon = HERO_STAT_ICON(s.label);
                            const inner = (
                                <>
                                    <Icon className="h-4 w-4 text-[#9D4CDD] shrink-0" />
                                    <span className="font-heading text-base sm:text-lg font-black text-white tabular-nums leading-none">{s.value}</span>
                                    <span className="text-[10px] sm:text-[11px] uppercase tracking-wider text-[#94A3B8] font-bold truncate">{s.label}</span>
                                </>
                            );
                            const cls = "rounded-xl border border-white/10 bg-black/45 backdrop-blur px-3 py-2.5 flex items-center gap-2 hover:border-[#9D4CDD]/70 hover:bg-black/60 transition-colors";
                            if (preview || !s.link) return <div key={i} className={cls} data-testid={`hero-stat-${i}`}>{inner}</div>;
                            return <Link key={i} to={s.link} className={cls} data-testid={`hero-stat-${i}`}>{inner}</Link>;
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
